import React, { useEffect, useState, useRef } from "react";
import { Input, Row, Col, message, Spin, Modal, Avatar, Button, Empty, Badge } from "antd";
import { useFetchChatList, useGetTripListForNewChat } from '../services/chat.service';
import IResponse from "../types/response";
import { formatDateTime, formatDateWithOrdinalSuffix, UseDebounce } from "../helpers/common";
import { IChatList, IChatDetail } from "../types/user";
import { SendOutlined, SearchOutlined, PhoneFilled } from "@ant-design/icons"
import { storage } from "../utils/storage";
import FirestoreDatabaseService, { getLiveMessages, listenToMultipleUnreadCounts, markMessagesAsRead } from "../firebase/firebase-service"
import { ITripListForNewChats } from "../types/chat";
import TextArea from "antd/es/input/TextArea";
import { useAuth } from "../context/AuthContext";

const Chats: React.FC = () => {
    const { Search } = Input;
    const [chatList, setChatList] = useState<IChatList[]>([]);
    const [chatDetail, setChatDetail] = useState<IChatDetail[]>([]);
    const [selectedChat, setSelectedChat] = useState<{ travellerId: string, agentId: string, agentName: string, chatId: string, travellerName: string, cityName: string, tripStartDate: string, tripEndDate: Date } | null>(null);
    const [loading, setIsLoading] = useState(true);
    const [loadingChatDetails, setIsLoadingChatDetails] = useState(false);
    const [loadingChats, setLoadingChats] = useState(true);
    const chatDetailContainerRef = useRef<HTMLDivElement>(null);
    const [showNewChatModal, setShowNewChatModal] = useState(false)
    const [creatingNewChat, setCreatingNewChat] = useState<string | null>(null)
    const [searchNewTripsValue, setSearchNewTripsValue] = useState("")
    const [searchChatsValue, setSearchChatsValue] = useState("")
    const [loadingNewTrips, setLoadingNewTrips] = useState(true)
    const [newTrips, setNewTrips] = useState<ITripListForNewChats[]>([])
    const [messageText, setMessageText] = useState("");
    const [sendingMessage, setSendingMessage] = useState(false);
    const [currentUserId, setCurrentUserId] = useState<string>("")
    const [currentChatId, setCurrentChatId] = useState<string | null>(null);
    const debouncedQuery = UseDebounce(searchNewTripsValue, 500);
    const debouncedQueryChats = UseDebounce(searchChatsValue, 500);
    const [unreadCounts, setUnreadCounts] = useState<{ [chatId: string]: number }>({});
    const unreadListenersRef = useRef<(() => void) | null>(null);

    const { hasPermission, permissionsLoaded, loadPermissions } = useAuth();

    useEffect(() => {
        loadPermissions();
    }, [permissionsLoaded]);

    const ChatsCreate = hasPermission("CHATS", "CREATE");
    const ChatsWrite = hasPermission("CHATS", "WRITE");

    useEffect(() => {
        const userid = storage.getUserId();
        if (userid) {
            setCurrentUserId(userid);
        }
    }, []);

    useEffect(() => {
        if (currentChatId) {
            setIsLoadingChatDetails(true);
            const unsubscribe = getLiveMessages(currentChatId, currentUserId, (messages) => {
                setChatDetail(messages);
                setIsLoadingChatDetails(false);
            });
            return () => unsubscribe();
        }
    }, [currentChatId])

    useEffect(() => {
        getChatList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQueryChats]);

    useEffect(() => {
        fetchTripList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery]);

    useEffect(() => {
        if (chatList.length > 0) {
            const firstChat = chatList[0];
            setSelectedChat(firstChat);
            setCurrentChatId(firstChat.chatId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatList]);

    useEffect(() => {
        if (chatDetailContainerRef.current) {
            chatDetailContainerRef.current.scrollTop = chatDetailContainerRef.current.scrollHeight;
        }
    }, [chatDetail]);

    useEffect(() => {
        if (chatList.length > 0 && currentUserId) {
            if (unreadListenersRef.current) {
                unreadListenersRef.current();
            }

            const chatIds = chatList.map(chat => chat.chatId);
            const unsubscribeAll = listenToMultipleUnreadCounts(
                chatIds,
                currentUserId,
                (chatId, count) => {
                    setUnreadCounts(prev => ({
                        ...prev,
                        [chatId]: count
                    }));
                }
            );

            unreadListenersRef.current = unsubscribeAll;
            return () => {
                if (unreadListenersRef.current) {
                    unreadListenersRef.current();
                }
            };
        }
    }, [chatList, currentUserId]);

    useEffect(() => {
        if (selectedChat && unreadCounts[selectedChat.chatId] > 0) {
            markChatAsRead(selectedChat.chatId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChat?.chatId]);

    const markChatAsRead = async (chatId: string) => {
        try {
            await markMessagesAsRead(chatId, currentUserId);
        } catch (error) {
            message.error("Error marking messages as read");
            console.error("Error marking messages as read:", error);
        }
    };

    const getChatList = () => {
        setIsLoading(true);
        setLoadingChats(true);
        fetchChatListMutation.mutate({ searchFilter: searchChatsValue });
    };

    const fetchChatListMutation = useFetchChatList({
        onSuccess: (response: IResponse) => {
            if (response.data.chatHistory != null && response.data.chatHistory !== undefined && Array.isArray(response.data.chatHistory)) {
                const sortedChatList = response.data.chatHistory.sort((a: IChatList, b: IChatList) => {
                    const dateA = new Date(a.lastUpdated);
                    const dateB = new Date(b.lastUpdated);
                    return dateB.getTime() - dateA.getTime();
                });
                setChatList(sortedChatList);
                if (sortedChatList.length <= 0) {
                    setChatDetail([]);
                }
            } else {
                console.error("Unexpected response format:");
            }
            setLoadingChats(false);
            setIsLoading(false);
        },
        onError: (error: any) => {
            message.error(error.message);
            setLoadingChats(false);
        }
    });

    const handleChatSelection = (chat: IChatList) => {
        setCurrentChatId(chat.chatId)
        setSelectedChat(chat);
    }

    const fetchTripList = () => {
        getTripListForNewChatMutation.mutate({ searchFilter: searchNewTripsValue })
    }

    const getTripListForNewChatMutation = useGetTripListForNewChat({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                setLoadingNewTrips(false);
                setNewTrips(response.data);
            }
        },
        onError: (err: any) => {
            message.error(err);
            setLoadingNewTrips(false);
        }
    })

    const handleNewChat = () => {
        setShowNewChatModal(true);
        setLoadingNewTrips(true);
        fetchTripList();
    }

    const CreateOrOpenChat = async (trip: ITripListForNewChats) => {
        setCreatingNewChat(trip.scheduleID);
        try {
            const existingChat = chatList.find(
                chat =>
                    chat.travellerId === trip.travellerID &&
                    chat.agentId === trip.agentID
            );

            if (existingChat) {
                setSelectedChat(existingChat);
                setShowNewChatModal(false);
                message.success("Chat opened");
                setCreatingNewChat(null);
                return;
            }

            let chatId = await FirestoreDatabaseService.getOrCreateChatIdForTrip(
                trip.travellerID,
                trip.agentID,
                currentUserId
            );

            if (chatId) {
                setShowNewChatModal(false);
                setCreatingNewChat(null);
                getChatList();
            }

        } catch (error) {
            message.error("Failed to create chat");
            setCreatingNewChat(null);
        }
    };

    const handleSendMessage = async () => {
        if (!messageText.trim() || !selectedChat) {
            return;
        }
        setSendingMessage(true);
        try {
            await FirestoreDatabaseService.sendChatMessage({
                chatId: selectedChat.chatId,
                senderId: currentUserId,
                senderName: "admin",
                text: messageText,
                participants: [currentUserId, selectedChat.agentId, selectedChat.travellerId]
            });

            const newMessage: IChatDetail = {
                senderId: currentUserId,
                senderName: "Admin",
                text: messageText,
                read: false,
                timestamp: Date.now(),
                type: "Text"
            };

            setChatDetail([...chatDetail, newMessage]);
            setMessageText("");
            message.success("Message sent");
        } catch (error) {
            console.error("Error sending message:", error);
            message.error("Failed to send message");
        } finally {
            setSendingMessage(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleSearchTrips = (value: string) => {
        setSearchNewTripsValue(value);
    };

    const getUnreadCountForChat = (chatId: string): number => {
        return unreadCounts[chatId] || 0;
    };

    const formatSecondsToTime = (value: string | number) => {
        const totalSeconds = Number(value);

        if (!totalSeconds || totalSeconds < 0) return "0 sec";

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 0) {
            return `${hours} hr ${minutes} min ${seconds} sec`;
        }

        if (minutes > 0) {
            return `${minutes} min ${seconds} sec`;
        }

        return `${seconds} sec`;
    };


    return (
        <div>
            <Row gutter={[16, 16]}>
                {/* ================= LEFT CHAT LIST ================= */}
                <Col xs={24} md={8} lg={6}>
                    {/* ✅ FIX: flex flex-col + overflow-hidden so children can scroll */}
                    <div className="bg-[#f5f5f5] p-5 h-[calc(100vh-175px)] flex flex-col overflow-hidden">

                        {/* ✅ FIX: flex-shrink-0 so header never gets compressed */}
                        <div className="mb-4 flex-shrink-0">
                            <div className="mb-5">
                                <h2 className="text-3xl font-semibold text-black">Chats</h2>
                            </div>
                            <div className="flex justify-between gap-5">
                                <div className="w-80 max-w-80">
                                    {ChatsCreate && (
                                        <Button
                                            type="primary"
                                            className="
                                                w-full h-10 mb-4
                                                bg-black text-white border border-black
                                                hover:!bg-transparent hover:!text-black hover:!border-black
                                                hover:transition hover:duration-500 hover:ease-in-out   
                                            "
                                            onClick={handleNewChat}
                                        >
                                            <span className="mr-1 text-lg">+</span> New Chat
                                        </Button>)}

                                    <Search
                                        placeholder="Search for traveller, or attendant"
                                        size="large"
                                        enterButton
                                        value={searchChatsValue}
                                        onChange={(val) => setSearchChatsValue(val.target.value)}
                                        className="search-field"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ✅ FIX: flex-1 + min-h-0 allows this div to scroll within flex parent */}
                        <div className="flex-1 overflow-y-auto pr-1 min-h-0">
                            {loadingChats ? (
                                <div className="flex justify-center items-center h-full">
                                    <Spin size="large" />
                                </div>
                            ) : chatList.length === 0 ? (
                                <Empty description="No chats available" className="mt-20" />
                            ) : (
                                chatList.map((item) => {
                                    const unreadCount = getUnreadCountForChat(item.chatId);

                                    return (
                                        <div
                                            key={item.chatId}
                                            onClick={() => handleChatSelection(item)}
                                            className={`mb-3 p-3 bg-white rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-all relative
                                                   ${selectedChat?.chatId === item.chatId
                                                    ? "bg-blue-50 border-2 border-blue-500"
                                                    : "hover:bg-gray-50"
                                                }`}
                                        >
                                            {unreadCount > 0 && (
                                                <Badge
                                                    count={unreadCount}
                                                    className="absolute top-2 right-2"
                                                    style={{
                                                        backgroundColor: '#1890ff',
                                                        boxShadow: '0 0 0 1px #fff'
                                                    }}
                                                />
                                            )}

                                            <p className={`text-base truncate ${unreadCount > 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'} pr-8`}>
                                                {item.travellerName}'s trip to {item.cityName}
                                            </p>
                                            <div className="flex justify-between mt-2">
                                                <p className={`text-sm ${unreadCount > 0 ? 'font-semibold text-gray-700' : 'font-medium text-gray-600'}`}>
                                                    {item.agentName}
                                                </p>
                                                <p className="text-gray-400 text-xs">
                                                    {formatDateTime(item.lastUpdated, "MMM DD, HH:mm")}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </Col>

                {/* ================= RIGHT CHAT PANEL ================= */}
                <Col xs={24} md={16} lg={18}>
                    <div className="bg-[#f5f5f5] p-5 h-[calc(100vh-175px)] flex flex-col">
                        <div className="flex-shrink-0">
                            {loading || !selectedChat ? (
                                <p className="text-2xl font-semibold user-name pb-5"></p>
                            ) : (
                                <p className="text-2xl font-semibold user-name pb-5">
                                    {selectedChat?.travellerName}'s trip to {selectedChat?.cityName} from {formatDateWithOrdinalSuffix(new Date(selectedChat?.tripStartDate ?? ""))} to {formatDateWithOrdinalSuffix(new Date(selectedChat?.tripEndDate ?? ""))}
                                </p>
                            )}
                        </div>

                        {/* Messages Scroll Area */}
                        <div ref={chatDetailContainerRef} className="flex-1 overflow-y-auto pr-4 -mr-5 mt-8 min-h-0">
                            {loadingChatDetails ? (
                                <div className="flex justify-center items-center h-full">
                                    <Spin size="large" />
                                </div>
                            ) : chatDetail.length === 0 ? (
                                <Empty description="No messages yet. Start the conversation!" />
                            ) : (
                                <div className="w-full">
                                    {chatDetail.map((message, index) => {
                                        const isCurrentUser = message.senderId === currentUserId;

                                        let senderLabel = message.senderName;
                                        if (message.senderId === currentUserId)
                                            senderLabel = "Admin (You)";
                                        else if (message.senderId === selectedChat?.agentId)
                                            senderLabel = `${selectedChat.agentName} (Agent)`;
                                        else if (message.senderId === selectedChat?.travellerId)
                                            senderLabel = `${selectedChat.travellerName} (Traveller)`;

                                        return (
                                            <div
                                                key={index}
                                                className={`flex items-start gap-4 w-full mb-4 ${message.senderId === currentUserId ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div className={"p-2.5 rounded-md shadow-custom bg-white"}>
                                                    <p className="text-md font-semibold">{senderLabel}</p>
                                                    {message.type === "VoiceCall" ? (
                                                        <div className="flex items-center gap-3 text-black py-2">
                                                            <div className="w-8 h-8 rounded-full bg-gray-500/20 flex items-center justify-center">
                                                                <PhoneFilled />
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <p className="text-sm font-semibold leading-4">Voice Call</p>
                                                                <p className="text-xs text-gray-300">
                                                                    {formatSecondsToTime(Number(message.text))}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p>{message.text}</p>
                                                    )}
                                                    <p className="text-[11px] text-end opacity-70">
                                                        {formatDateTime(message.timestamp, "DD MMM, HH:mm")}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Message Input */}
                        {ChatsWrite && (
                            <div className="flex-shrink-0 pt-4 mt-4 border-t-2 border-gray-300 bg-white rounded-lg p-3 shadow-custom">
                                <div className="flex gap-2 items-center">
                                    <TextArea
                                        value={messageText}
                                        onChange={(e) => setMessageText(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Type your message..."
                                        autoSize={{ minRows: 1, maxRows: 1 }}
                                        disabled={sendingMessage}
                                        className="flex-1 h-10 px-4 resize-none"
                                        style={{
                                            height: '40px',
                                            minHeight: '40px',
                                            maxHeight: '40px',
                                            paddingTop: '8px',
                                            paddingBottom: '8px'
                                        }}
                                    />
                                    <Button
                                        type="primary"
                                        icon={<SendOutlined />}
                                        onClick={handleSendMessage}
                                        loading={sendingMessage}
                                        disabled={!messageText.trim()}
                                        className="h-10 px-6 bg-black text-white border border-black hover:!bg-transparent hover:!text-black hover:!border-black hover:transition hover:duration-500 hover:ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        Send
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* New Chat Modal */}
            {showNewChatModal && (
                <Modal
                    title={
                        <div className="text-xl font-semibold">
                            Create or Open Chat
                        </div>
                    }
                    open={true}
                    onCancel={creatingNewChat ? undefined : () => setShowNewChatModal(false)}
                    footer={null}
                    width={600}
                    closable={!creatingNewChat}
                    maskClosable={!creatingNewChat}
                >
                    <div className="py-4">
                        <Input
                            placeholder="Search by traveller name, city, or agent"
                            value={searchNewTripsValue}
                            onChange={(e) => handleSearchTrips(e.target.value)}
                            prefix={<SearchOutlined className="text-gray-400" />}
                            size="large"
                            className="mb-4"
                            disabled={!!creatingNewChat}
                        />

                        {loadingNewTrips ? (
                            <div className="flex justify-center py-20">
                                <Spin size="large" />
                            </div>
                        ) : newTrips.length === 0 ? (
                            <Empty description="No trips found" className="py-10" />
                        ) : (
                            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                                {newTrips.map((trip) => {
                                    const existingChat = chatList.find(
                                        chat =>
                                            chat.travellerId === trip.travellerID &&
                                            chat.agentId === trip.agentID
                                    );

                                    return (
                                        <div
                                            key={trip.scheduleID}
                                            className="flex items-center justify-between border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-blue-300 transition-all"
                                        >
                                            <div className="flex-1">
                                                <div className="font-semibold text-lg text-gray-800">
                                                    {trip.travellerName}'s Trip to {trip.cityName}
                                                </div>
                                                <div className="text-sm text-gray-600 mt-1">
                                                    Agent: {trip.agentName}
                                                </div>
                                                <div className="text-sm text-gray-500 mt-1">
                                                    Date: {formatDateTime(trip.scheduleDate, "DD/MM/YYYY")}
                                                </div>
                                                {existingChat && (
                                                    <div className="text-xs text-blue-600 font-medium mt-1">
                                                        ✓ Chat already exists
                                                    </div>
                                                )}
                                            </div>

                                            <Button
                                                type="primary"
                                                className={`ml-4 h-10 px-6 
                                                    bg-black text-white border border-black
                                                    hover:!bg-transparent hover:!text-black hover:!border-black
                                                    hover:transition hover:duration-500 hover:ease-in-out
                                                    ${creatingNewChat === trip.scheduleID ? 'opacity-60 cursor-not-allowed' : ''}
                                                `}
                                                onClick={() => CreateOrOpenChat(trip)}
                                                loading={creatingNewChat === trip.scheduleID}
                                                disabled={!!creatingNewChat && creatingNewChat !== trip.scheduleID}
                                            >
                                                {existingChat ? 'Open Chat' : 'Start Chat'}
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Chats;